import React, { useState, useEffect } from 'react';
import { 
  ClipboardPaste, 
  Download, 
  Music, 
  Loader2, 
  AlertCircle, 
  ArrowLeft,
  User,
  Calendar,
  Clock,
  ChevronRight,
  Search,
  X,
  Hash
} from 'lucide-react';
import './index.css';
import { Helmet } from 'react-helmet-async';
import { translations } from './translations';
import { getPosts, getPostBySlug, getCategories, urlFor } from './sanityClient';
import { PortableText } from '@portabletext/react';
import { 
  Routes, 
  Route, 
  useParams, 
  useNavigate, 
  NavLink, 
  useLocation,
  useSearchParams
} from 'react-router-dom';

import folderIcon from './assets/folder.png';
import ribbonIcon from './assets/ribbon.png';
import cdIcon from './assets/cd.png';

const ScrollToTop = () => {
  const { pathname, search, hash } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, search, hash]);
  return null;
};

const LANGS = ['en', 'es', 'fr', 'de', 'it', 'pt', 'ru', 'tr', 'ar', 'hi', 'zh', 'ja', 'ko', 'th', 'vi', 'id', 'ur', 'bn'];

const BLOG_SLUG_MAPS = {
  "definitive-guide-download-tiktok-no-watermark-2026": {
    en: "definitive-guide-download-tiktok-no-watermark-2026",
    es: "guia-descargar-tiktok-sin-marca-agua-hd",
    fr: "guide-ultime-telechargement-tiktok-sans-filigrane",
    de: "tiktok-videos-ohne-wasserzeichen-herunterladen-leitfaden",
    ar: "guide-download-tiktok-no-watermark-arabic",
    hi: "download-tiktok-no-watermark-guide-hindi",
    pt: "como-baixar-tiktok-sem-marca-agua-guia-br",
    ru: "polnoe-rukovodstvo-skachat-tiktok-bez-znakov",
    ja: "tiktok-video-no-logo-guide-japan",
    zh: "tiktok-no-watermark-download-guide-china"
  },
  "tiktok-seo-2026-ranking-fyp": {
    en: "tiktok-seo-2026-ranking-fyp",
    ar: "tiktok-seo-2026-arabic-guide"
  }
};

const REVERSE_SLUG_MAP = {};
Object.entries(BLOG_SLUG_MAPS).forEach(([enSlug, langMap]) => {
  Object.entries(langMap).forEach(([lang, slug]) => {
    REVERSE_SLUG_MAP[slug] = { enSlug, langMap };
  });
});

const getSlugForLanguage = (currentSlug, targetLang) => {
  if (REVERSE_SLUG_MAP[currentSlug]) {
    const { langMap } = REVERSE_SLUG_MAP[currentSlug];
    return langMap[targetLang] || null;
  }
  let baseSlug = currentSlug;
  LANGS.forEach(l => {
    if (l !== 'en' && currentSlug.endsWith(`-${l}`)) {
      baseSlug = currentSlug.substring(0, currentSlug.length - l.length - 1);
    }
  });
  return targetLang === 'en' ? baseSlug : `${baseSlug}-${targetLang}`;
};


const TIKWM_PROXY = '/api/tikwm';

const getFullUrl = (url) => {
  if (!url) return url;
  if (url.includes('tikwm.com')) {
    const parts = url.split('tikwm.com');
    const path = parts[1] || '';
    return `/tikwm-proxy${path}`;
  }
  if (url.startsWith('/')) return `/tikwm-proxy${url}`;
  return url;
};

const SEOContent = ({ t, keyword }) => {
  const [openFaq, setOpenFaq] = useState(null);
  const toggleFaq = (index) => setOpenFaq(openFaq === index ? null : index);

  const isStory = keyword?.includes('story');

  const faqs = isStory ? [
    { q: "How to download TikTok stories anonymously?", a: "To download a TikTok story anonymously, just copy the share link of the story or the profile URL, paste it into TikGrab, and hit download. We don't require any login, so your view remains completely anonymous." },
    { q: "Can I view TikTok stories without a TikTok account?", a: "Yes! TikGrab's TikTok Story Viewer allows you to watch any public story without having an account or being logged in. The creator will not see that you viewed their story." },
    { q: "Is it possible to save TikTok stories in HD quality?", a: "Absolutely. TikGrab extracts the original high-definition source of the story, allowing you to save it in its original resolution (up to 1080p or 4K depending on the upload) without the TikTok watermark." },
    { q: t.faq1Q, a: t.faq1A },
    { q: t.faq4Q, a: t.faq4A },
    { q: t.faq5Q, a: t.faq5A }
  ] : [
    { q: t.faq1Q, a: t.faq1A },
    { q: t.faq2Q, a: t.faq2A },
    { q: t.faq3Q, a: t.faq3A },
    { q: t.faq4Q, a: t.faq4A },
    { q: t.faq5Q, a: t.faq5A },
    { q: t.faq6Q, a: t.faq6A },
    { q: t.faq7Q, a: t.faq7A },
    { q: t.faq8Q, a: t.faq8A },
    { q: t.faq9Q, a: t.faq9A },
    { q: t.faq10Q, a: t.faq10A },
    { q: t.faq11Q, a: t.faq11A },
  ];

  return (
    <section className="seo-content-section" id="how-to-save">
      <div className="seo-intro">
        <h2>{isStory ? `The Ultimate TikTok ${keyword?.includes('viewer') ? 'Story Viewer' : 'Story Downloader'}` : (t.seoTextTitle || "Download TikTok videos without watermark for free")}</h2>
        <p>{isStory ? `TikGrab is the fastest way to ${keyword?.includes('viewer') ? 'anonymously view' : 'save'} TikTok stories. Our tool removes the watermark and gives you a clean HD file suitable for re-purposing or offline viewing. No account registration or app installation is required.` : (t.seoTextDesc || "TikGrab is a free TikTok video downloader that allows you to download TikTok videos without watermark in MP4 format and HD, Full HD, or 4K quality.")}</p>
        
        <h3>Personal Backup and Fair Use</h3>
        <p>TikGrab is designed strictly as an educational and personal backup utility. We believe that creators and individuals should have the ability to archive their own content or save public educational media for offline study and fair use. We do not host or store any media on our servers. Please ensure that you have the explicit right or permission from the original creator before downloading and using any content from TikTok.</p>
      </div>

      <div className="instructions-section">
        <h3>{isStory ? `How to ${keyword.includes('viewer') ? 'view' : 'download'} TikTok stories?` : (t.howToDownloadTitle || "How to download TikTok videos without watermark?")}</h3>
        <p>{t.howToDownloadDesc || "TikGrab has made it easy for you. Just follow the steps below."}</p>
        <div className="steps-container">
          <div className="step-item">
            <h4>{t.step1Title || `Step 1: Copy ${isStory ? 'Story' : 'Video'} Link`}</h4>
            <p>{t.step1Desc || `Find the ${isStory ? 'story' : 'video'} on TikTok, tap Share, and select 'Copy Link'. Alternatively, copy the Profile URL of the creator.`}</p>
          </div>
          <div className="step-item">
            <h4>{t.step2Title || `Step 2: Paste in ${isStory ? 'Story Saver' : 'TikGrab'}`}</h4>
            <p>{t.step2Desc || "Return to TikGrab, paste the URL into the input field above, and click the Download button."}</p>
          </div>
          <div className="step-item">
            <h4>{t.step3Title || `Step 3: Save ${isStory ? 'Story' : 'Video'}`}</h4>
            <p>{t.step3Desc || `Choose your preferred format and save the ${isStory ? 'story' : 'video'} instantly to your device.`}</p>
          </div>
        </div>
      </div>

      <div className="faq-section">
        <h3>{t.faqTitle || "Frequently asked questions"}</h3>
        <div className="faq-accordion">
          {faqs.map((faq, index) => (
            <div key={index} className={`faq-accordion-item ${openFaq === index ? 'open' : ''}`}>
              <div className="faq-question" onClick={() => toggleFaq(index)}>
                <span>{faq.q}</span>
                <span className="faq-icon">{openFaq === index ? '−' : '+'}</span>
              </div>
              {openFaq === index && <div className="faq-answer"><p>{faq.a}</p></div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AboutUs = ({ currentLang, t }) => {
  const pageTitle = `${t.aboutTitle || "About Us"} - TikGrab Media Labs`;
  const pageDesc = "Learn about TikGrab: our mission, editorial standards, research on vertical video SEO, and commitment to fair-use creator media archiving.";
  return (
    <div className="content-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={`https://tikgrab.app/${currentLang}/about`} />
        {LANGS.map(l => (
          <link key={l} rel="alternate" hreflang={l} href={`https://tikgrab.app/${l}/about`} />
        ))}
        <link rel="alternate" hreflang="x-default" href="https://tikgrab.app/en/about" />
      </Helmet>
      <h1>About TikGrab Media Labs</h1>
      <p>TikGrab is an independent digital media publication, educational resource center, and creator utility founded in 2024. Our mission is to empower content creators, social media marketing teams, video editors, and academic researchers with reliable tools and actionable research to master the fast-moving short-form video ecosystem.</p>
      
      <h2>Our Mission & Core Values</h2>
      <p>Short-form video has rapidly become the dominant storytelling medium on the modern web. However, creators frequently struggle with platform-imposed compression artifacts, watermark penalties when syndicating original work across multiple platforms, and opaque ranking algorithms. TikGrab was created to solve these challenges through transparency, engineering excellence, and ethical utility design.</p>

      <h2>What We Provide</h2>
      <p>TikGrab delivers high-value services across two foundational pillars:</p>
      <ul>
        <li><strong>Original Educational Research & Guides:</strong> Our team conducts continuous empirical testing on vertical video algorithms (TikTok, Instagram Reels, YouTube Shorts), bitrate optimization standards, audio extraction methods, and creator monetization programs. All articles published in our blog are written, fact-checked, and updated by real media practitioners.</li>
        <li><strong>Free Fair-Use Content Archiving Utilities:</strong> We provide fast, lossless web utilities that allow creators to archive their original creations, download high-definition master files without watermarks for cross-platform editing, and extract crystal-clear 320kbps MP3 audio for sound design workflows.</li>
      </ul>

      <h2>Our Commitment to Fair Use & Intellectual Property</h2>
      <p>TikGrab firmly respects international copyright law and creator ownership. We strictly prohibit the unauthorized downloading or distribution of copyrighted commercial material. TikGrab functions entirely client-side and via direct pass-through requests; we do not host, store, or cache media files on any server. Our utilities are built strictly for personal content archiving, fair-use educational analysis, and creator multi-platform workflow management.</p>

      <h2>Editorial Independence & Integrity</h2>
      <p>We are completely independent and are not affiliated, associated, authorized, endorsed by, or in any official way connected with TikTok, ByteDance Ltd., Meta Platforms Inc., or Alphabet Inc. All tutorials and analytical commentary represent our team's honest technical observations and independent industry benchmarks.</p>

      <h2>Contact Our Team</h2>
      <p>We welcome inquiries, feedback, and research collaborations from journalists, researchers, and creators. Reach our editorial and technical team anytime at <a href="mailto:contact@tikgrab.app">contact@tikgrab.app</a> or through our <a href={`/${currentLang}/contact`}>Contact Us</a> page.</p>
    </div>
  );
};

const ContactUs = ({ currentLang, t }) => {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', category: 'General Support', subject: '', message: '' });

  const pageTitle = `${t.contactTitle || "Contact Us"} - TikGrab Support & Editorial`;
  const pageDesc = "Get in touch with the TikGrab team for technical assistance, DMCA copyright notices, partnership inquiries, or editorial feedback.";

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="content-page contact-page-wrapper">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={`https://tikgrab.app/${currentLang}/contact`} />
        {LANGS.map(l => (
          <link key={l} rel="alternate" hreflang={l} href={`https://tikgrab.app/${l}/contact`} />
        ))}
        <link rel="alternate" hreflang="x-default" href="https://tikgrab.app/en/contact" />
      </Helmet>
      <h1>{t.contactTitle || "Contact Us"}</h1>
      <p className="contact-intro">
        Have a question about our creator tools, an editorial suggestion for our guides, or need technical assistance? Our dedicated support team is here to assist you.
      </p>

      <div className="contact-channels">
        <div className="contact-channel-card">
          <h4>Technical & User Support</h4>
          <a href="mailto:support@tikgrab.app">support@tikgrab.app</a>
          <p>For bug reports, browser issues, or download assistance. Mon–Fri, 9am–6pm EST.</p>
        </div>
        <div className="contact-channel-card">
          <h4>DMCA & Legal Notices</h4>
          <a href="mailto:dmca@tikgrab.app">dmca@tikgrab.app</a>
          <p>For copyright inquiries, ownership verification, and content takedown requests.</p>
        </div>
        <div className="contact-channel-card">
          <h4>Partnerships & Editorial</h4>
          <a href="mailto:contact@tikgrab.app">contact@tikgrab.app</a>
          <p>For guest contributions, press inquiries, and advertising collaborations.</p>
        </div>
      </div>

      {submitted ? (
        <div className="contact-success-box">
          <h3>Thank You for Reaching Out!</h3>
          <p>Your message has been received by our support team. We review all inquiries thoroughly and will respond to your email address ({formData.email || 'provided'}) within 24–48 business hours.</p>
        </div>
      ) : (
        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">{t.contactName || "Your Full Name"}</label>
              <input 
                type="text" 
                id="name" 
                placeholder="Jane Doe" 
                required 
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">{t.contactEmail || "Email Address"}</label>
              <input 
                type="email" 
                id="email" 
                placeholder="name@example.com" 
                required 
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Inquiry Topic</label>
              <select 
                id="category" 
                style={{ padding: '12px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem', background: '#fff' }}
                value={formData.category}
                onChange={e => setFormData({ ...formData, category: e.target.value })}
              >
                <option value="General Support">General Support & Feedback</option>
                <option value="Technical Issue">Technical Bug or Tool Issue</option>
                <option value="DMCA Request">DMCA / Copyright Inquiry</option>
                <option value="Advertising">Advertising & Business Partnership</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="subject">{t.contactSubject || "Subject"}</label>
              <input 
                type="text" 
                id="subject" 
                placeholder="Brief summary of your inquiry" 
                required
                value={formData.subject}
                onChange={e => setFormData({ ...formData, subject: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="message">{t.contactMessage || "Detailed Message"}</label>
            <textarea 
              id="message" 
              rows="6" 
              placeholder="Please provide details so we can best assist you..." 
              required
              value={formData.message}
              onChange={e => setFormData({ ...formData, message: e.target.value })}
            ></textarea>
          </div>

          <button type="submit" className="contact-submit-btn">Send Message</button>
        </form>
      )}
    </div>
  );
};

const PrivacyPolicy = () => {
  const location = useLocation();
  const currentLang = location.pathname.split('/')[1] || 'en';
  const pageTitle = "Privacy Policy - TikGrab.app";
  const pageDesc = "Privacy Policy for TikGrab. Comprehensive details on data handling, Google AdSense cookies, GDPR, and CCPA consumer rights.";
  return (
    <div className="content-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={`https://tikgrab.app/${currentLang}/privacy`} />
        {LANGS.map(l => (
          <link key={l} rel="alternate" hreflang={l} href={`https://tikgrab.app/${l}/privacy`} />
        ))}
        <link rel="alternate" hreflang="x-default" href="https://tikgrab.app/en/privacy" />
      </Helmet>
      <h1>Privacy Policy</h1>
      <p>Last Updated: October 2026</p>
      <p>At TikGrab (accessible from https://tikgrab.app), the privacy of our visitors is of paramount importance. This Privacy Policy document details the types of information collected and recorded by TikGrab and how we utilize it in strict compliance with global privacy standards.</p>
      
      <h2>1. Log Files and Technical Data</h2>
      <p>TikGrab follows a standard procedure of utilizing log files. These files log visitors when they access websites. The information collected includes internet protocol (IP) addresses, browser type, Internet Service Provider (ISP), date and time stamp, referring/exit pages, and possibly the number of clicks. These are not linked to any personally identifiable information. The purpose of this information is to analyze trends, administer the site, track users' movement on the website, and gather broad demographic data for site maintenance and performance optimization.</p>
      
      <h2>2. Cookies, Web Beacons and Advertising Partners</h2>
      <p>Like any modern web property, TikGrab uses 'cookies'. These cookies are used to store information including visitors' preferences and the pages on the website that the visitor accessed or visited. The information is used to optimize user experience by customizing our web page content based on browser type and other preferences.</p>

      <h2>3. Google AdSense and DoubleClick DART Cookies</h2>
      <p>Google is a third-party vendor on our site. It uses cookies, known as DART cookies, to serve ads to our site visitors based upon their visit to www.tikgrab.app and other sites on the internet.</p>
      <ul>
        <li>Third-party vendors, including Google, use cookies to serve ads based on a user's prior visits to your website or other websites on the internet.</li>
        <li>Google's use of advertising cookies enables it and its partners to serve ads to users based on their visits to our site and/or other sites across the World Wide Web.</li>
        <li>Visitors may choose to decline the use of DART cookies by visiting the Google Ad and Content Network Privacy Policy at <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noreferrer">https://policies.google.com/technologies/ads</a>.</li>
        <li>You can also opt out of third-party advertising cookies through the Network Advertising Initiative (NAI) opt-out page at <a href="https://optout.networkadvertising.org/" target="_blank" rel="noreferrer">https://optout.networkadvertising.org/</a> or the Digital Advertising Alliance at <a href="https://www.aboutads.info/choices/" target="_blank" rel="noreferrer">https://www.aboutads.info/choices/</a>.</li>
      </ul>

      <h2>4. Zero Media Storage Policy</h2>
      <p>TikGrab does not store, archive, or retain any videos, photos, or audio files on our servers. All media streams processed through our utilities are delivered directly from official Content Delivery Networks on an on-demand basis. We maintain zero databases of user download histories or pasted links.</p>

      <h2>5. CCPA Privacy Rights (Do Not Sell My Personal Information)</h2>
      <p>Under the California Consumer Privacy Act (CCPA), California consumers have the right to:</p>
      <ul>
        <li>Request that a business disclose the categories and specific pieces of personal data collected about consumers.</li>
        <li>Request that a business delete any personal data about the consumer that a business has collected.</li>
        <li>Request that a business that sells a consumer's personal data not sell the consumer's personal data. TikGrab does not sell personal data to any third party.</li>
      </ul>

      <h2>6. GDPR Data Protection Rights</h2>
      <p>We ensure that you are fully aware of all your data protection rights under the General Data Protection Regulation (GDPR). Every user is entitled to the following:</p>
      <ul>
        <li><strong>The right to access:</strong> You have the right to request copies of your personal data.</li>
        <li><strong>The right to rectification:</strong> You have the right to request that we correct any information you believe is inaccurate.</li>
        <li><strong>The right to erasure:</strong> You have the right to request that we erase your personal data under certain conditions.</li>
        <li><strong>The right to restrict processing:</strong> You have the right to request that we restrict the processing of your personal data.</li>
      </ul>

      <h2>7. Children's Online Privacy Protection Act (COPPA)</h2>
      <p>TikGrab does not knowingly collect any Personal Identifiable Information from children under the age of 13. If you think that your child provided this kind of information on our website, we strongly encourage you to contact us immediately and we will promptly remove such information from our records.</p>

      <h2>8. Contact Our Privacy Officer</h2>
      <p>If you have additional questions or require more information about our Privacy Policy, do not hesitate to contact us at <a href="mailto:privacy@tikgrab.app">privacy@tikgrab.app</a>.</p>
    </div>
  );
};

const TermsOfService = () => {
  const location = useLocation();
  const currentLang = location.pathname.split('/')[1] || 'en';
  const pageTitle = "Terms of Service - TikGrab.app";
  const pageDesc = "Terms of Service for TikGrab. By accessing and using TikGrab, you accept and agree to these terms.";
  return (
    <div className="content-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={`https://tikgrab.app/${currentLang}/terms`} />
        {LANGS.map(l => (
          <link key={l} rel="alternate" hreflang={l} href={`https://tikgrab.app/${l}/terms`} />
        ))}
        <link rel="alternate" hreflang="x-default" href="https://tikgrab.app/en/terms" />
      </Helmet>
      <h1>Terms of Service</h1>
      <p>By accessing and using TikGrab, you accept and agree to be bound by the terms and provision of this agreement.</p>
      
      <h2>1. Usage and Restrictions</h2>
      <p>TikGrab is a tool intended solely for personal, non-commercial use. Users are strictly responsible for ensuring they have the legal right to download and save the content they process through our site. We do not support, encourage, or condone copyright infringement. You agree not to use this service to download copyrighted material without the explicit consent of the owner.</p>
      
      <h2>2. Disclaimer of Warranties</h2>
      <p>TikGrab is provided on an "as is" and "as available" basis without any warranties of any kind, either express or implied. We do not guarantee that the site will always be safe, secure, or error-free, or that the site will always function without disruptions, delays, or imperfections.</p>
      
      <h2>3. Limitation of Liability</h2>
      <p>In no event shall TikGrab, its directors, employees, or partners, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of or inability to access or use the service.</p>
      
      <h2>4. Third-Party Links and Ads</h2>
      <p>Our service may contain links to third-party web sites or services (including advertisers like Google AdSense) that are not owned or controlled by TikGrab. TikGrab has no control over, and assumes no responsibility for, the content, privacy policies, or practices of any third party web sites or services.</p>
    </div>
  );
};

const Disclaimer = () => {
  const location = useLocation();
  const currentLang = location.pathname.split('/')[1] || 'en';
  const pageTitle = "Disclaimer & DMCA Policy - TikGrab.app";
  const pageDesc = "Disclaimer & DMCA Copyright Policy for TikGrab. Non-affiliation notice with TikTok and ByteDance.";
  return (
    <div className="content-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={`https://tikgrab.app/${currentLang}/disclaimer`} />
        {LANGS.map(l => (
          <link key={l} rel="alternate" hreflang={l} href={`https://tikgrab.app/${l}/disclaimer`} />
        ))}
        <link rel="alternate" hreflang="x-default" href="https://tikgrab.app/en/disclaimer" />
      </Helmet>
      <h1>Disclaimer & DMCA Policy</h1>
      <p>Last Updated: August 2026</p>
      
      <h2>1. Non-Affiliation Disclaimer</h2>
      <p>TikGrab is an independent online web utility. TikGrab is NOT affiliated, associated, authorized, endorsed by, or in any official way connected with TikTok, ByteDance Ltd., or any of their subsidiaries or affiliates. The official TikTok website can be found at <a href="https://www.tiktok.com" target="_blank" rel="noreferrer">https://www.tiktok.com</a>.</p>
      <p>All product names, logos, brands, trademarks, and registered trademarks belong to their respective owners. Reference to any third-party brands is solely for descriptive purposes and does not imply endorsement or partnership.</p>

      <h2>2. Content Ownership and Fair Use</h2>
      <p>TikGrab does not host, store, or archive any media files or videos on its servers. All video and audio media files are served directly from official TikTok CDN servers. Users are strictly responsible for respecting copyright laws and downloading media for personal, non-commercial offline viewing or fair-use educational purposes only.</p>

      <h2>3. DMCA Copyright Notice & Takedown Requests</h2>
      <p>TikGrab respects the intellectual property rights of others. If you are a copyright owner or an agent thereof and believe that any content accessible through our platform infringes upon your copyright, please send a DMCA notice to our designated contact point via our <a href={`/${currentLang}/contact`}>Contact Us page</a> with the following details:</p>
      <ul>
        <li>Identification of the copyrighted work claimed to have been infringed.</li>
        <li>Direct URL link(s) to the specific content on TikTok.</li>
        <li>Your contact information including full legal name, physical address, telephone number, and email address.</li>
        <li>A statement that you have a good faith belief that use of the material is not authorized by the copyright owner.</li>
      </ul>
    </div>
  );
};

const NotFound = ({ currentLang = 'en' }) => {
  const navigate = useNavigate();
  return (
    <div className="content-page" style={{ textAlign: 'center', padding: '80px 20px' }}>
      <Helmet>
        <title>404 - Page Not Found - TikGrab.app</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <AlertCircle size={48} style={{ color: '#0B63F6', marginBottom: '20px' }} />
      <h2>Page Not Found</h2>
      <p style={{ margin: '15px 0 30px', color: '#666' }}>
        Sorry, the page you are looking for does not exist or has been moved.
      </p>
      <button 
        className="download-button-cta" 
        onClick={() => navigate(`/${currentLang}/`)}
        style={{ padding: '10px 24px', fontSize: '16px' }}
      >
        Back to Home
      </button>
    </div>
  );
};


const BlogSecondaryMenu = ({ currentLang }) => {
  return (
    <div className="blog-secondary-nav">
      <div className="blog-nav-container">
        <NavLink to={`/${currentLang}/`} className="blog-nav-item"><Hash size={16} /> TIKGRAB</NavLink>
        <NavLink to={`/${currentLang}/tiktok-story-saver`} className="blog-nav-item">STORY SAVER</NavLink>
        <NavLink to={`/${currentLang}/tiktok-story-viewer`} className="blog-nav-item">STORY VIEWER</NavLink>
        <NavLink to={`/${currentLang}/contact`} className="blog-nav-item">CONTACT US</NavLink>
        <NavLink to={`/${currentLang}/about`} className="blog-nav-item">ABOUT US</NavLink>
      </div>
    </div>
  );
};

const BlogSidebar = ({ currentLang, categories, searchQuery, setSearchQuery, selectedCategory }) => {
  const navigate = useNavigate();
  
  return (
    <aside className="blog-sidebar">
      <div className="sidebar-widget search-widget">
        <div className="search-input-wrapper">
          <input 
            type="text" 
            placeholder="Search posts..." 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Search size={18} className="search-icon" />
          {searchQuery && <X size={18} className="clear-search" onClick={() => setSearchQuery('')} />}
        </div>
      </div>

      <div className="sidebar-widget category-widget">
        <h3 className="widget-title">Categories</h3>
        <ul className="category-list">
          <li 
            className={!selectedCategory ? 'active' : ''} 
            onClick={() => navigate(`/${currentLang}/blog`)}
          >
            All Posts
          </li>
          {categories.map(cat => (
            <li 
              key={cat.slug} 
              className={selectedCategory === cat.slug ? 'active' : ''}
              onClick={() => navigate(`/${currentLang}/blog?category=${cat.slug}`)}
            >
              <span>{cat.title}</span>
              <span className="cat-count">{cat.count}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="sidebar-widget premium-widget">
        <div className="promo-box">
          <h4>Download TikToks without watermark</h4>
          <p>The fastest way to save TikTok videos in HD quality.</p>
          <button onClick={() => navigate(`/${currentLang}/`)}>Go to Downloader</button>
        </div>
      </div>
    </aside>
  );
};

const BlogList = ({ currentLang }) => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const selectedCategory = searchParams.get('category');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getPosts(selectedCategory, searchQuery, currentLang),
      getCategories(currentLang)
    ]).then(([postsData, catsData]) => {
      setPosts(postsData || []);
      setCategories(catsData || []);
      setLoading(false);
    }).catch(err => {
      console.error(err);
      setLoading(false);
    });
  }, [selectedCategory, searchQuery, currentLang]);

  const categoryTitle = selectedCategory ? (categories.find(c => c.slug === selectedCategory)?.title) : null;
  const pageTitle = `${categoryTitle || "TikGrab Blog & Guides"} - TikGrab.app`;
  const pageDesc = "Read our official guides and articles on TikTok SEO, video downloading, and social commerce hacks.";
  const canonicalUrl = `https://tikgrab.app/${currentLang}/blog`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonicalUrl} />
        {LANGS.map(l => (
          <link key={l} rel="alternate" hreflang={l} href={`https://tikgrab.app/${l}/blog`} />
        ))}
        <link rel="alternate" hreflang="x-default" href="https://tikgrab.app/en/blog" />
      </Helmet>
      <BlogSecondaryMenu currentLang={currentLang} />
      <section className="blog-container-layout">
        <main className="blog-main-content">
          <div className="blog-header-section">
            <h1 className="blog-title">
              {selectedCategory ? categories.find(c => c.slug === selectedCategory)?.title : "TikGrab Blog"}
            </h1>
            <div className="blog-title-underline"></div>
          </div>

          {loading ? (
            <div className="loading-container"><Loader2 size={40} className="spin-icon" /><p>Loading blog posts...</p></div>
          ) : (
            <div className="blog-grid">
              {posts.length > 0 ? posts.map(post => (
                <div key={post._id || (typeof post.slug === 'object' ? post.slug?.current : post.slug) || Math.random()} className="blog-card" onClick={() => navigate(`/${currentLang}/blog/${typeof post.slug === 'object' ? post.slug?.current : post.slug}`)}>
                  <div className="blog-card-image">
                    <img src={post.mainImage ? urlFor(post.mainImage).width(600).url() : "https://via.placeholder.com/600x400"} alt={post.title} />
                    {post.category && <span className="blog-category-badge">{typeof post.category === 'string' ? post.category : (post.category?.title || 'General')}</span>}
                  </div>
                  <div className="blog-card-content">
                    <div className="blog-meta-top">
                      <span><Calendar size={14} /> {post.date}</span>
                      <span><Clock size={14} /> {post.readTime}</span>
                    </div>
                    <h3>{post.title}</h3>
                    <p>{post.excerpt}</p>
                    <div className="blog-footer">
                      <span className="read-more-link">Read More <ChevronRight size={14} /></span>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="no-posts-msg">
                  <AlertCircle size={40} />
                  <p>No blog posts found matching your criteria.</p>
                  <button onClick={() => {setSearchQuery(''); navigate(`/${currentLang}/blog`)}}>Reset Filters</button>
                </div>
              )}
            </div>
          )}
        </main>
        
        <BlogSidebar 
          currentLang={currentLang} 
          categories={categories} 
          searchQuery={searchQuery} 
          setSearchQuery={setSearchQuery}
          selectedCategory={selectedCategory}
        />
      </section>
    </>
  );
};

const BlogPost = ({ currentLang }) => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getPostBySlug(slug),
      getCategories()
    ]).then(([postData, catsData]) => {
      setPost(postData);
      setCategories(catsData || []);
      setLoading(false);
    }).catch(err => {
      console.error(err);
      setLoading(false);
    });
  }, [slug]);

  if (loading) return <div className="loading-container"><Loader2 size={40} className="spin-icon" /><p>Loading article...</p></div>;
  if (!post) {
    return (
      <div className="content-page" style={{ textAlign: 'center', padding: '80px 20px' }}>
        <Helmet>
          <title>Article Not Found - TikGrab.app</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <AlertCircle size={48} style={{ color: '#0B63F6', marginBottom: '20px' }} />
        <h2>Post Not Found</h2>
        <p style={{ margin: '15px 0 30px', color: '#666' }}>Sorry, the article you are looking for does not exist or has been moved.</p>
        <button 
          className="download-button-cta" 
          onClick={() => navigate(`/${currentLang}/blog`)}
          style={{ padding: '10px 24px', fontSize: '16px' }}
        >
          Back to Blog
        </button>
      </div>
    );
  }

  const postTitle = `${post.title} - TikGrab.app`;
  const postDesc = post.excerpt || "Read our latest article on TikGrab Blog.";
  const canonicalUrl = `https://tikgrab.app/${currentLang}/blog/${post.slug.current}`;

  return (
    <>
      <Helmet>
        <title>{postTitle}</title>
        <meta name="description" content={postDesc} />
        <link rel="canonical" href={canonicalUrl} />
        {LANGS.map(l => {
          const targetSlug = getSlugForLanguage(post.slug.current, l);
          if (!targetSlug) return null;
          return (
            <link key={l} rel="alternate" hreflang={l} href={`https://tikgrab.app/${l}/blog/${targetSlug}`} />
          );
        })}
        <link rel="alternate" hreflang="x-default" href={`https://tikgrab.app/en/blog/${getSlugForLanguage(post.slug.current, 'en')}`} />
      </Helmet>
      <BlogSecondaryMenu currentLang={currentLang} />
      <div className="blog-container-layout">
        <article className="blog-post-page">
          <button className="back-to-blog" onClick={() => navigate(`/${currentLang}/blog`)}>
            <ArrowLeft size={18} /> Back to Blog
          </button>
          <header className="post-header">
            <div className="post-meta-tags">
              {post.category && <span className="post-category-tag">{typeof post.category === 'string' ? post.category : (post.category?.title || 'General')}</span>}
              <span className="post-meta-item"><Calendar size={14} /> {post.date}</span>
              <span className="post-meta-item"><User size={14} /> {post.author || 'Admin'}</span>
              <span className="post-meta-item"><Clock size={14} /> {post.readTime || '5 min read'}</span>
            </div>
            <h1>{post.title}</h1>
            {post.mainImage && <img src={urlFor(post.mainImage).width(1200).url()} alt={post.title} className="post-hero-image" />}
          </header>
          <div className="post-content">
            <PortableText value={post.body} />
          </div>
          <div className="post-footer-cta">
            <h3>Want to download TikTok videos without watermark?</h3>
            <p>Join millions of users who use TikGrab for high-quality, secure downloads.</p>
            <button onClick={() => navigate(`/${currentLang}/`)} className="download-button-cta">Try TikGrab Now</button>
          </div>
        </article>

        <BlogSidebar 
          currentLang={currentLang} 
          categories={categories} 
          searchQuery="" 
          setSearchQuery={() => navigate(`/${currentLang}/blog`)}
          selectedCategory={post.categorySlug}
        />
      </div>
    </>
  );
};

const FeaturedGuides = ({ currentLang }) => {
  const [guides, setGuides] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    getPosts(null, '', currentLang).then(data => {
      if (data && data.length > 0) {
        setGuides(data.slice(0, 3));
      }
    }).catch(() => {});
  }, [currentLang]);

  const fallbackGuides = [
    {
      title: "TikTok SEO Algorithm Secrets: How to Rank #1 on TikTok Search in 2026",
      slug: "tiktok-seo-algorithm-secrets-rank-number-one",
      excerpt: "Master TikTok search engine optimization: learn ranking factors, keyword placement in captions, and spoken audio SEO.",
      category: "TikTok SEO & Growth",
      readTime: "10 min read"
    },
    {
      title: "How to Extract and Download TikTok Audio & Sounds as MP3 (320kbps High Quality)",
      slug: "how-to-download-tiktok-audio-mp3-high-quality",
      excerpt: "Learn how to extract high-fidelity 320kbps MP3 audio from any TikTok video for editing in CapCut.",
      category: "Content Strategy",
      readTime: "8 min read"
    },
    {
      title: "TikTok Video Dimensions, Aspect Ratio & Bitrate Guide (2026 Standards)",
      slug: "tiktok-video-dimensions-aspect-ratio-bitrate-guide",
      excerpt: "Complete technical specifications for 9:16 vertical resolution (1080x1920) and optimal export bitrate settings.",
      category: "Content Strategy",
      readTime: "9 min read"
    }
  ];

  const displayList = guides.length > 0 ? guides : fallbackGuides;

  return (
    <section className="featured-guides-section">
      <div className="featured-guides-header">
        <span className="featured-guides-pill">Educational Resources</span>
        <h2>Creator Guides & Growth Tutorials</h2>
        <p>Explore our latest in-depth research on vertical video SEO, algorithm mechanics, and high-definition editing workflows.</p>
      </div>
      <div className="featured-guides-grid">
        {displayList.map((g, idx) => {
          const slugStr = typeof g.slug === 'object' ? g.slug?.current : g.slug;
          return (
            <article 
              key={idx} 
              className="featured-guide-card"
              onClick={() => navigate(`/${currentLang}/blog/${slugStr}`)}
            >
              <div className="featured-guide-badge">{g.category || 'Guide'}</div>
              <h3>{g.title}</h3>
              <p>{g.excerpt}</p>
              <div className="featured-guide-footer">
                <span><Clock size={14} /> {g.readTime || '8 min read'}</span>
                <span className="read-more-link">Read Full Guide →</span>
              </div>
            </article>
          );
        })}
      </div>
      <div className="featured-guides-cta-row">
        <button className="view-all-guides-btn" onClick={() => navigate(`/${currentLang}/blog`)}>
          Browse All Articles & Tutorials
        </button>
      </div>
    </section>
  );
};

const Downloader = ({ currentLang, customT }) => {
  const { keyword } = useParams();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [videoData, setVideoData] = useState(null);
  const [isDownloading, setIsDownloading] = useState(null);
  const location = useLocation();
  const t = customT || translations[currentLang] || translations.en;

  useEffect(() => {
    if (location.state && location.state.manualUrl) {
      handleDownload(location.state.manualUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setUrl(text);
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
    }
  };

  const handleDownload = async (manualUrl = null) => {
    const targetUrl = (manualUrl && typeof manualUrl === 'string') ? manualUrl : url;
    if (!targetUrl) {
      setError(t.placeholder);
      return;
    }
    setLoading(true);
    setError(null);
    setVideoData(null);
    try {
      const encodedUrl = encodeURIComponent(targetUrl);
      const apiUrl = `${TIKWM_PROXY}?url=${encodedUrl}&count=12&cursor=0&web=1&hd=1`;
      const response = await fetch(apiUrl);
      const data = await response.json();
      if (data.code === 0 && data.data) {
        const d = data.data;
        // CDN URLs are on tiktokcdn.com - no proxy needed, use directly
        // Only proxy URLs that are on tikwm.com domain
        d.cover = getFullUrl(d.cover);
        d.play = getFullUrl(d.play);
        d.hdplay = getFullUrl(d.hdplay);
        if (d.author && d.author.avatar) d.author.avatar = getFullUrl(d.author.avatar);
        if (d.music_info && d.music_info.play) d.music_info.play = getFullUrl(d.music_info.play);
        setVideoData(d);
        if (manualUrl) window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setError(data.msg || "Failed to parse video.");
      }
    } catch (err) {
      setError("Network error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (fileUrl, suggestedFilename) => {
    if (isDownloading) return;
    setIsDownloading(fileUrl);
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = suggestedFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
       window.open(fileUrl + (fileUrl.includes('?') ? '&' : '?') + 'dl=1', '_blank');
    } finally {
      setIsDownloading(null);
    }
  };

  return (
    <>
      <section className="hero">
        <h1>{t.heroTitle}</h1>
        <div className="input-wrapper">
          <input type="text" className="url-input" placeholder={t.placeholder} value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleDownload()} />
          <button className="paste-button" onClick={handlePaste} disabled={loading}><ClipboardPaste size={16} />{t.paste}</button>
          <button className="download-button" onClick={handleDownload} disabled={loading}>{loading ? <Loader2 size={20} className="spin-icon" /> : t.download}</button>
        </div>
        {error && <div className="error-message"><AlertCircle size={18} /><span>{error}</span></div>}
      </section>
      {videoData && (
        <section className="result-section">
          <div className="result-card">
            <div className="video-preview">
              <video src={videoData.play} poster={videoData.cover} controls referrerPolicy="no-referrer" className="thumbnail" />
              <div className="author-info">
                <img src={videoData.author.avatar} alt="Author" className="author-avatar" referrerPolicy="no-referrer" />
                <div className="author-details"><span className="author-name">{videoData.author.nickname}</span><span className="author-id">@{videoData.author.unique_id}</span></div>
              </div>
            </div>
            <div className="download-options">
              <h3>{videoData.title || "TikTok Video"}</h3>
              <div className="stats-row"><span className="stat-pill">❤️ {videoData.digg_count}</span><span className="stat-pill">💬 {videoData.comment_count}</span><span className="stat-pill">🔁 {videoData.share_count}</span></div>
              <div className="action-buttons">
                <button className="action-btn primary-btn" onClick={() => downloadFile(videoData.play, `tiktok-no-watermark.mp4`)} disabled={!!isDownloading}><Download size={18} />{t.noWatermark}</button>
                {videoData.music_info && <button className="action-btn audio-btn" onClick={() => downloadFile(videoData.music_info.play, `tiktok-audio.mp3`)} disabled={!!isDownloading}><Music size={18} />{t.audioMp3}</button>}
              </div>
            </div>
          </div>
        </section>
      )}
      <section className="features-section">
        <h2>{t.featureUnlimited}</h2>
        <div className="features-grid">
          <div className="feature-card"><div className="feature-icon-wrapper"><img src={folderIcon} alt={t.featureUnlimited} /></div><h3>{t.featureUnlimited}</h3><p>{t.featureUnlimitedDesc}</p></div>
          <div className="feature-card"><div className="feature-icon-wrapper"><img src={ribbonIcon} alt={t.featureNoWatermark} /></div><h3>{t.featureNoWatermark}</h3><p>{t.featureNoWatermarkDesc}</p></div>
          <div className="feature-card"><div className="feature-icon-wrapper"><img src={cdIcon} alt={t.featureMp4Mp3} /></div><h3>{t.featureMp4Mp3}</h3><p>{t.featureMp4Mp3Desc}</p></div>
        </div>
      </section>
      <SEOContent t={t} keyword={keyword} />
      <FeaturedGuides currentLang={currentLang} />
    </>
  );
};

const Popular = ({ currentLang }) => {
  const [popularVideos, setPopularVideos] = useState([]);
  const [popLoading, setPopLoading] = useState(false);
  const navigate = useNavigate();
  const t = translations[currentLang] || translations.en;

  useEffect(() => { fetchPopularVideos(); }, []);

  const fetchPopularVideos = async () => {
    setPopLoading(true);
    try {
      const response = await fetch(`${TIKWM_PROXY}/api/feed/list?region=US&count=12&cursor=0`);
      const data = await response.json();
      // Fix: TikWM API returns the array directly in 'data'
      if (data.code === 0 && data.data) {
        setPopularVideos(Array.isArray(data.data) ? data.data : (data.data.videos || []));
      }
    } catch (err) { console.error(err); } finally { setPopLoading(false); }
  };

  const pageTitle = `${t.popularVideos} - TikGrab.app`;
  const pageDesc = "Watch and download trending popular TikTok videos without watermark for free in HD quality.";

  return (
    <section className="popular-section">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={`https://tikgrab.app/${currentLang}/popular`} />
        {LANGS.map(l => (
          <link key={l} rel="alternate" hreflang={l} href={`https://tikgrab.app/${l}/popular`} />
        ))}
        <link rel="alternate" hreflang="x-default" href="https://tikgrab.app/en/popular" />
      </Helmet>
      <div className="popular-header"><h2>{t.popularVideos}</h2><button className="back-btn" onClick={() => navigate(`/${currentLang}/`)}><ArrowLeft size={18} /> {t.backToHome}</button></div>
      {popLoading ? <div className="loading-container"><Loader2 size={40} className="spin-icon" /><p>{t.loading}</p></div> : (
        <div className="video-grid">
          {popularVideos.length > 0 ? popularVideos.map(vid => (
            <div key={vid.video_id} className="video-item" onClick={() => navigate(`/${currentLang}/`, { state: { manualUrl: `https://www.tiktok.com/@${vid.author?.unique_id || 'user'}/video/${vid.video_id}` } })}>
              <div className="video-item-cover"><img src={getFullUrl(vid.cover)} alt={vid.title} referrerPolicy="no-referrer" /><div className="video-item-stats"><span>❤️ {(vid.digg_count / 1000).toFixed(1)}k</span></div></div>
              <div className="video-item-info"><p className="video-item-title">{vid.title || "TikTok Video"}</p><div className="video-item-author"><img src={getFullUrl(vid.author?.avatar)} alt={vid.author?.nickname} referrerPolicy="no-referrer" /><span>@{vid.author?.unique_id}</span></div></div>
            </div>
          )) : <div className="no-results-msg">No popular videos found for this region.</div>}
        </div>
      )}
    </section>
  );
};

const VALID_KEYWORDS = ['tiktok-story-saver', 'tiktok-story-downloader', 'tiktok-story-viewer', 'tiktok-downloader'];

const LandingPage = ({ toolKeyword }) => {
  const { lang, keyword: paramKeyword } = useParams();
  const keyword = toolKeyword || paramKeyword;
  const currentLang = translations[lang] ? lang : 'en';
  const t = translations[currentLang];
  
  if (keyword && !VALID_KEYWORDS.includes(keyword)) {
    return <NotFound currentLang={currentLang} />;
  }

  let pageTitle = `${t.heroTitle} Without Watermark - TikGrab.app`;
  let pageDesc = `${t.bottomHeading}. ${t.featureNoWatermarkDesc}`;
  let heroTitle = t.heroTitle;
  let placeholder = t.placeholder;

  if (keyword === 'tiktok-story-saver' || keyword === 'tiktok-story-downloader') {
    pageTitle = `TikTok Story Downloader - Save TikTok Stories Anonymously - TikGrab.app`;
    pageDesc = `Download TikTok stories without watermark in HD quality using TikGrab. Our TikTok story saver is free, fast, and anonymous.`;
    heroTitle = "TikTok Story Downloader";
    placeholder = "Paste TikTok Story Link Here";
  } else if (keyword === 'tiktok-story-viewer') {
    pageTitle = `TikTok Story Viewer - View TikTok Stories Anonymously - TikGrab.app`;
    pageDesc = `Anonymously view and download TikTok stories without watermark. No login required. View TikTok stories in HD for free.`;
    heroTitle = "TikTok Story Viewer";
    placeholder = "Enter TikTok Profile URL or Story Link";
  } else if (keyword === 'tiktok-downloader') {
    pageTitle = `TikTok Downloader Online - TikGrab.app`;
    heroTitle = "TikTok Video Downloader";
  }

  // Override translations for the specific keywords
  const themedT = { ...t, heroTitle, placeholder };

  const canonicalPath = keyword ? `/${currentLang}/${keyword}` : `/${currentLang}/`;
  const canonicalUrl = `https://tikgrab.app${canonicalPath}`;

  const webAppSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "TikGrab",
    "url": canonicalUrl,
    "applicationCategory": "MultimediaApplication",
    "operatingSystem": "All",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": pageDesc
  };

  return (
    <main>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonicalUrl} />
        {LANGS.map(l => {
          const altPath = keyword ? `/${l}/${keyword}` : `/${l}/`;
          return (
            <link key={l} rel="alternate" hreflang={l} href={`https://tikgrab.app${altPath}`} />
          );
        })}
        <link rel="alternate" hreflang="x-default" href={`https://tikgrab.app${keyword ? `/en/${keyword}` : '/en/'}`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:image" content="https://tikgrab.app/favicon.svg" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content="https://tikgrab.app/favicon.svg" />
        <script type="application/ld+json">{JSON.stringify(webAppSchema)}</script>
      </Helmet>
      <Downloader currentLang={currentLang} customT={themedT} />
    </main>
  );
};

const LanguageSelector = ({ currentLang, onLangChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);
  
  const languages = [
    { code: 'en', name: 'English', flag: 'gb' },
    { code: 'es', name: 'Español', flag: 'es' },
    { code: 'fr', name: 'Français', flag: 'fr' },
    { code: 'de', name: 'Deutsch', flag: 'de' },
    { code: 'it', name: 'Italiano', flag: 'it' },
    { code: 'pt', name: 'Português', flag: 'pt' },
    { code: 'ru', name: 'Русский', flag: 'ru' },
    { code: 'tr', name: 'Türkçe', flag: 'tr' },
    { code: 'ar', name: 'العربية', flag: 'sa' },
    { code: 'hi', name: 'हिन्दी', flag: 'in' },
    { code: 'zh', name: '汉语', flag: 'cn' },
    { code: 'ja', name: '日本語', flag: 'jp' },
    { code: 'ko', name: '한국어', flag: 'kr' },
    { code: 'th', name: 'ไทย', flag: 'th' },
    { code: 'vi', name: 'Tiếng Việt', flag: 'vn' },
    { code: 'id', name: 'Indonesia', flag: 'id' },
    { code: 'ur', name: 'اردو', flag: 'pk' },
    { code: 'bn', name: 'বাংলা', flag: 'bd' }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const current = languages.find(l => l.code === currentLang) || languages[0];

  return (
    <div className="custom-lang-selector" ref={dropdownRef}>
      <button className="lang-trigger" onClick={() => setIsOpen(!isOpen)}>
        <img src={`https://flagcdn.com/w40/${current.flag}.png`} alt={current.name} width="20" />
        <span>{current.name}</span>
        <ChevronRight size={14} className={`arrow ${isOpen ? 'open' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="lang-dropdown">
          <div className="lang-grid-selector">
            {languages.map(lang => (
              <div 
                key={lang.code} 
                className={`lang-option ${lang.code === currentLang ? 'active' : ''}`}
                onClick={() => {
                  onLangChange(lang.code);
                  setIsOpen(false);
                }}
              >
                <img src={`https://flagcdn.com/w40/${lang.flag}.png`} alt={lang.name} width="20" />
                <span>{lang.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const CookieConsent = ({ currentLang }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('tikgrab_cookie_consent');
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('tikgrab_cookie_consent', 'accepted');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="cookie-consent-banner" style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, 
      backgroundColor: '#f8f9fa', padding: '15px 20px', 
      boxShadow: '0 -2px 10px rgba(0,0,0,0.1)', zIndex: 9999,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      flexWrap: 'wrap', gap: '10px'
    }}>
      <div style={{ flex: '1 1 300px', fontSize: '14px', color: '#333' }}>
        We use cookies to enhance your browsing experience, serve personalized ads or content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies. 
        <a href={`/${currentLang}/privacy`} style={{ marginLeft: '5px', color: '#0B63F6', textDecoration: 'underline' }}>Read our Privacy Policy</a>.
      </div>
      <div style={{ display: 'flex', gap: '10px' }}>
        <button onClick={handleAccept} style={{
          backgroundColor: '#0B63F6', color: 'white', border: 'none', 
          padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold'
        }}>Accept All</button>
      </div>
    </div>
  );
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentLang = location.pathname.split('/')[1] || 'en';
  const t = translations[currentLang] || translations.en;

  const handleLangChange = (newLang) => {
    const segments = location.pathname.split('/');
    if (segments.length > 1) {
      segments[1] = newLang;
      navigate(segments.join('/'));
    } else {
      navigate(`/${newLang}/`);
    }
  };

  const isRTL = currentLang === 'ar' || currentLang === 'ur';

  return (
    <div className={`app-container ${isRTL ? 'rtl-layout' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <ScrollToTop />
      <CookieConsent currentLang={currentLang} />
      <header className="top-header">
        <div onClick={() => navigate(`/${currentLang}/`)} className="logo-container" style={{cursor:'pointer', display: 'flex', alignItems: 'center', gap: '10px'}}>
          <svg width="40" height="40" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="playGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#1DA1FF" />
                <stop offset="50%" stopColor="#0B63F6" />
                <stop offset="100%" stopColor="#003FBD" />
              </linearGradient>
              <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#7CD4FF" />
                <stop offset="100%" stopColor="#1DA1FF" />
              </linearGradient>
            </defs>
            {/* Play Button Background */}
            <path d="M30 20C24.4772 20 20 24.4772 20 30V90C20 95.5228 24.4772 100 30 100H40L100 65C104 62.5 104 57.5 100 55L40 20H30Z" fill="url(#playGradient)" />
            {/* Down Arrow */}
            <path d="M45 40V70H35L50 85L65 70H55V40H45Z" fill="white" />
          </svg>
          <div style={{display: 'flex', flexDirection: 'column', lineHeight: '1', fontWeight: '800'}}>
             <span style={{fontSize: '18px', color: '#1DA1FF'}}>tik</span>
             <span style={{fontSize: '24px', color: '#003FBD'}}>Grab</span>
          </div>
        </div>
        <nav className="header-center-links">
          <NavLink to={`/${currentLang}/`} className="header-link">{t.downloader || 'Downloader'}</NavLink>
          <NavLink to={`/${currentLang}/popular`} className="header-link">{t.popularVideos}</NavLink>
          <NavLink to={`/${currentLang}/blog`} className="header-link">{t.blog || 'Blog'}</NavLink>
        </nav>
        <LanguageSelector currentLang={currentLang} onLangChange={handleLangChange} />
      </header>

      <Routes>
        <Route path="/:lang" element={<LandingPage />} />
        <Route path="/:lang/" element={<LandingPage />} />
        <Route path="/:lang/popular" element={<Popular currentLang={currentLang} />} />
        <Route path="/:lang/blog" element={<BlogList currentLang={currentLang} />} />
        <Route path="/:lang/blog/:slug" element={<BlogPost currentLang={currentLang} />} />
        <Route path="/:lang/about" element={<AboutUs currentLang={currentLang} t={t} />} />
        <Route path="/:lang/contact" element={<ContactUs currentLang={currentLang} t={t} />} />
        <Route path="/:lang/privacy" element={<PrivacyPolicy />} />
        <Route path="/:lang/terms" element={<TermsOfService />} />
        <Route path="/:lang/disclaimer" element={<Disclaimer />} />
        <Route path="/:lang/dmca" element={<Disclaimer />} />
        <Route path="/:lang/tiktok-downloader" element={<LandingPage toolKeyword="tiktok-downloader" />} />
        <Route path="/:lang/tiktok-story-saver" element={<LandingPage toolKeyword="tiktok-story-saver" />} />
        <Route path="/:lang/tiktok-story-downloader" element={<LandingPage toolKeyword="tiktok-story-downloader" />} />
        <Route path="/:lang/tiktok-story-viewer" element={<LandingPage toolKeyword="tiktok-story-viewer" />} />
        <Route path="/:lang/:keyword" element={<LandingPage />} />
        <Route path="*" element={<NotFound currentLang={currentLang} />} />
      </Routes>

      <footer className="footer-rich">
        <div className="footer-cols">
          <div className="footer-col">
            <h4>{t.tools || 'Tools'}</h4>
            <NavLink to={`/${currentLang}/tiktok-downloader`} onClick={() => window.scrollTo(0,0)}>TikTok Downloader</NavLink>
            <NavLink to={`/${currentLang}/tiktok-story-saver`} onClick={() => window.scrollTo(0,0)}>TikTok Story Saver</NavLink>
            <NavLink to={`/${currentLang}/tiktok-story-viewer`} onClick={() => window.scrollTo(0,0)}>TikTok Story Viewer</NavLink>
          </div>
          <div className="footer-col">
            <h4>{t.legal || 'Legal'}</h4>
            <NavLink to={`/${currentLang}/terms`}>{t.termsTitle || 'Terms of Service'}</NavLink>
            <NavLink to={`/${currentLang}/privacy`}>{t.privacyTitle || 'Privacy Policy'}</NavLink>
            <NavLink to={`/${currentLang}/disclaimer`}>Disclaimer & DMCA</NavLink>
          </div>
          <div className="footer-col">
            <h4>{t.resources || 'Resources'}</h4>
            <NavLink to={`/${currentLang}/blog`}>{t.blogAndGuides || 'Blog & Guides'}</NavLink>
            <NavLink to={`/${currentLang}/about`}>{t.aboutUs || 'About Us'}</NavLink>
            <NavLink to={`/${currentLang}/contact`}>{t.contactUs || 'Contact Us'}</NavLink>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2026 TikGrab. All rights reserved. TikGrab is not affiliated with TikTok or ByteDance Ltd.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
